import {DisallowedMoveReason} from 'audit/AuditDAO';
import BenchlingConfig from 'BenchlingConfig';
import {RationaleConfigValues} from 'tenants/admin/data-modifications/DataModsConfigConstants';
import {HTTPHeaders} from 'util/Net';

// Header key and enum must be kept in sync with the backend in benchling/audit/reason_code_and_comment.py
export const REASON_CODE_AND_COMMENT_HEADER_KEY = 'X-BENCH-REASON-CODE-AND-COMMENTS';

export enum ReasonCodeAndCommentType {
  DATA_MODIFICATIONS = 'dataMods',
  BYPASS_VALIDATION_ERROR = 'bypassValidationError',
}

/**
 * Example: {
 *   dataMods: {reasonCode: 'typo', reasonComment: 'whoops'},
 *   bypassValidationError: {reasonCode: 'other', reasonComment: 'other comment'},
 * }
 * Note that not every top-level key of enum ReasonCodeAndCommentType is required
 */
export type ReasonCodeAndComments = Partial<{
  [key in ReasonCodeAndCommentType]: {
    reasonCode: string;
    reasonComment: string;
  };
}>;

/**
 * Custom http header where the value is a base64 encoded string of a
 * stringified ReasonCodeAndComments JSON object
 */
export interface DataModificationsHeader extends HTTPHeaders {
  [REASON_CODE_AND_COMMENT_HEADER_KEY]: string;
}

/**
 *  Creates the data modifications HTTP header with the given reason code and reason comment.
 *  The header is a base64 encoded ASCII string of a JSON object.
 *  Since the text could have Unicode characters, we need to convert the JSON object to a UTF-8 byte array
 *  before encoding it to base64 ASCII string. For more info, see
 *  https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
 * @param reasonCode the data mods reason code from the dropdown
 * @param reasonComment an optional data mods comment
 * @param additionalReasonCodeAndComments additional reason code and comments to include in the header
 */
export function createDataModificationsHeaders(
  reasonCode: string,
  reasonComment: string = '',
  additionalReasonCodeAndComments?: DataModificationsHeader
): DataModificationsHeader {
  return encodeReasonCodeAndCommentHeader(
    ReasonCodeAndCommentType.DATA_MODIFICATIONS,
    reasonCode,
    reasonComment,
    additionalReasonCodeAndComments
  );
}

export function createBypassValidationErrorHeaders(
  reasonCode: string,
  reasonComment: string = ''
): DataModificationsHeader {
  return encodeReasonCodeAndCommentHeader(
    ReasonCodeAndCommentType.BYPASS_VALIDATION_ERROR,
    reasonCode,
    reasonComment
  );
}

/**
 * Given Reason Code and Comment strings, encodes them and returns a custom HTTP header
 */
export function encodeReasonCodeAndCommentHeader(
  reasonCodeAndCommentType: ReasonCodeAndCommentType,
  reasonCode: string,
  reasonComment: string = '',
  additionalReasonCodeAndComments?: DataModificationsHeader
): DataModificationsHeader {
  let reasonCodeAndCommentsJson: ReasonCodeAndComments = {
    [reasonCodeAndCommentType]: {reasonCode, reasonComment},
  };

  if (additionalReasonCodeAndComments) {
    // have to decode the existing header before merging the additional reason code and comments,
    // but this is easier than passing around a mix of encoded or decoded headers
    const otherReasonCodeAndComments = decodeReasonCodeAndCommentHeader(additionalReasonCodeAndComments);
    reasonCodeAndCommentsJson = {...reasonCodeAndCommentsJson, ...otherReasonCodeAndComments};
  }

  const jsonString = JSON.stringify(reasonCodeAndCommentsJson);

  return {
    [REASON_CODE_AND_COMMENT_HEADER_KEY]: encodeString(jsonString),
  };
}

function encodeString(value: string): string {
  const utf8Bytes = new TextEncoder().encode(value);

  const bytesToBase64String = (bytes: Uint8Array): string => {
    const binString = Array.from(bytes, (byte: number) => String.fromCodePoint(byte)).join('');
    return btoa(binString);
  };
  return bytesToBase64String(utf8Bytes);
}

export function decodeReasonCodeAndCommentHeader(header: DataModificationsHeader): ReasonCodeAndComments {
  const jsonString = decodeString(header[REASON_CODE_AND_COMMENT_HEADER_KEY]);
  return JSON.parse(jsonString) as ReasonCodeAndComments;
}

function decodeString(value: string): string {
  const base64ToBytes = (base64: string): Uint8Array => {
    const binString = atob(base64);
    return Uint8Array.from(binString, (m) => m.codePointAt(0) as number);
  };
  const bytes = base64ToBytes(value);
  return new TextDecoder().decode(bytes);
}

export function formatDataModsMoveDisallowedErrorMessage(disallowedApiIds: {
  [apiId: string]: DisallowedMoveReason;
}): string {
  const apiIds = Object.entries(disallowedApiIds);
  // format the first api id
  let message =
    `${apiIds.length > 1 ? `item ${apiIds[0][0]}` : 'this item'} from a project ` +
    `with data modifications rationale ` +
    `${apiIds[0][1] === DisallowedMoveReason.DISABLED_TO_ENABLED ? 'disabled' : 'enabled'}` +
    ` to a project with data modifications rationale ${
      apiIds[0][1] === DisallowedMoveReason.DISABLED_TO_ENABLED ? 'enabled' : 'disabled'
    }`;

  // format the rest of the api ids if any
  if (apiIds.length > 1) {
    for (let i = 1; i < apiIds.length; i++) {
      message +=
        ` and item ${apiIds[i][0]} from a project with data modifications rationale ${
          apiIds[i][1] === DisallowedMoveReason.DISABLED_TO_ENABLED ? 'disabled' : 'enabled'
        }` +
        ` to a project with data modifications rationale ${
          apiIds[i][1] === DisallowedMoveReason.DISABLED_TO_ENABLED ? 'enabled' : 'disabled'
        }`;
    }
  }

  return `You cannot move ${message}. Please reach out to your tenant admin with any questions.`;
}

export function areDataModsEnabledAnywhere(): boolean {
  return (
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_SETTING != null &&
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_SETTING !== RationaleConfigValues.DISABLED_FOR_TENANT
  );
}

export function isProjectLevelDataModsEnabled(): boolean {
  return (
    BenchlingConfig.AUDITING_DATA_MODIFICATIONS_SETTING ===
    RationaleConfigValues.ENABLED_FOR_SPECIFIC_PROJECTS
  );
}
